import { navigateTo } from '@/Client/State/navigationRef';
import { KaiIcon } from '@kaiizen/kai-shiki/react';
import { type ComponentType, type ReactNode, useState } from 'react';
import { useLocation } from 'react-router';
import { NavItemButton } from './NavItemButton';
import { NavItemCornerBracket } from './NavItemCornerBracket';
import { DEFAULT_SIDEBAR_CONFIG, type SidebarConfig } from './sidebarConfig';
import { DEFAULT_LOGO_HEIGHT, DEFAULT_SIDEBAR_STYLES, type SidebarNavItemVisualState, type SidebarStyles } from './sidebarStyles';

export type AdminChromeIconComponent = ComponentType<{
  color?: string;
  size?: number;
}>;

export type SidebarProps = {
  logo?: ReactNode;
  logoAriaLabel?: string;
  /** Height (px) of the logo heading/button. Defaults to 60. */
  logoHeight?: number;
  onLogoClick?: () => void;
  config?: SidebarConfig;
  footer?: ReactNode;
  styles?: SidebarStyles;
};

const getNavItemVisualState = (
  selected: boolean,
  isHovered: boolean,
  isPressed: boolean,
): SidebarNavItemVisualState => {
  if (selected) return 'selected';
  if (isPressed) return 'pressed';
  if (isHovered) return 'hover';
  return 'default';
};

export const Sidebar = ({
  logo = <KaiIcon size={39} />,
  logoAriaLabel = 'Kai',
  logoHeight = DEFAULT_LOGO_HEIGHT,
  onLogoClick = () => navigateTo('/dashboard'),
  config = DEFAULT_SIDEBAR_CONFIG,
  footer = `v${__APP_VERSION__}`,
  styles = DEFAULT_SIDEBAR_STYLES,
}: SidebarProps) => {
  const location = useLocation();
  const [hoveredItemLabel, setHoveredItemLabel] = useState<string | null>(null);
  const [pressedItemLabel, setPressedItemLabel] = useState<string | null>(null);

  return (
    <div css={styles.root}>
      <h1 css={styles.logoHeading(logoHeight)}>
        <button
          type={'button'}
          css={styles.logoButton}
          onClick={onLogoClick}
          aria-label={logoAriaLabel}
        >
          {logo}
        </button>
      </h1>

      <div css={styles.navItems}>
        {config.navItems.map(({ label, Icon, isSelected, onClick }) => {
          const selected = isSelected(location.pathname);
          const isHovered = hoveredItemLabel === label;
          const isPressed = pressedItemLabel === label;
          const visualState = getNavItemVisualState(selected, isHovered, isPressed);

          return (
            <NavItemButton
              key={label}
              label={label}
              ariaLabel={label}
              title={label}
              itemCss={[styles.navItemBase, styles.navItemByState[visualState]]}
              onClick={() => {
                setPressedItemLabel(null);
                onClick();
              }}
              onMouseEnter={() => setHoveredItemLabel(label)}
              onMouseLeave={() => {
                setHoveredItemLabel(current => (current === label ? null : current));
                setPressedItemLabel(current => (current === label ? null : current));
              }}
              onMouseDown={() => setPressedItemLabel(label)}
              onMouseUp={() => setPressedItemLabel(current => (current === label ? null : current))}
              onBlur={() => {
                setHoveredItemLabel(current => (current === label ? null : current));
                setPressedItemLabel(current => (current === label ? null : current));
              }}
            >
              {
                <span css={styles.iconFrame.frame}>
                  <Icon size={visualState === 'selected' ? 20 : 24} />
                  {visualState === 'selected' && <NavItemCornerBracket styles={styles.iconFrame} />}
                </span>
              }
              <span css={[styles.navLabelBase, styles.navLabelByState[visualState]]}>{label}</span>
            </NavItemButton>
          );
        })}
      </div>

      <div css={styles.footer}>{footer}</div>
    </div>
  );
};
